import React from "react";
import type { Control, FieldErrors } from "react-hook-form";
import { Controller } from "react-hook-form";
import type { OptionType } from "components/inputs";
import {
  Select,
  DatePicker,
  DATE_TO_STRING,
  STRING_TO_DATE,
} from "components/inputs";
import MultiSelect from "components/inputs/Select/MultiSelect";

export type TasksFilters = {
  client_id: OptionType | null;
  report_type: OptionType | null;
  assignee_ids: OptionType[];
  status: OptionType | null;
  service_type: OptionType | null;
  created_after: string | null;
  created_before: string | null;
  started_after: string | null;
  started_before: string | null;
  completed_after: string | null;
  completed_before: string | null;
};

type ExtendedOptionType = OptionType & {
  clientId?: string;
};

type TasksReportFiltersProps = {
  control: Control<TasksFilters>;
  errors: FieldErrors<TasksFilters>;
  watchedStartDate: string | null;
  watchedEndDate: string | null;
  watchedReportType: OptionType | null;
  clientOptions: ExtendedOptionType[];
  assigneeOptions: OptionType[];
  statusOptions: OptionType[];
  serviceTypeOptions: OptionType[];
  isClientsFetching: boolean;
  isAssigneesFetching: boolean;
  disabled: boolean;
  debouncedSetClientSearchTerm: (value: string) => void;
  debouncedSetAssigneeSearchTerm: (value: string) => void;
};

// Configuration for different report types
const REPORT_CONFIGS = {
  TASKS: {
    filters: [
      "client",
      "assignees",
      "status",
      "service_type",
      "created_dates",
      "started_dates",
      "datetime_completed",
    ],
    customComponents: [],
  },
  // You can add other report types here in the future
} as const;

type ReportType = keyof typeof REPORT_CONFIGS;

const TasksReportFilters: React.FC<TasksReportFiltersProps> = ({
  control,
  errors,
  watchedReportType,
  clientOptions,
  assigneeOptions,
  statusOptions,
  serviceTypeOptions,
  isClientsFetching,
  isAssigneesFetching,
  debouncedSetClientSearchTerm,
  debouncedSetAssigneeSearchTerm,
}) => {
  const currentReportType = (watchedReportType?.value as ReportType) || "TASKS";
  const config = REPORT_CONFIGS[currentReportType] || REPORT_CONFIGS.TASKS;

  const shouldShowFilter = (filterName: string) => {
    return config.filters.includes(filterName as never);
  };

  return (
    <>
      {/* Client filter */}
      {shouldShowFilter("client") && (
        <div className="filters-row">
          <Controller
            name="client_id"
            control={control}
            rules={{ required: "Client is required" }}
            render={({ field: { name, onChange } }) => (
              <Select
                name={name}
                label="Client Name"
                options={clientOptions}
                errors={errors}
                placeholder="Search..."
                noOptionsMessage={({ inputValue }) =>
                  !inputValue
                    ? "Begin typing to search for clients..."
                    : isClientsFetching
                      ? "Searching..."
                      : "No clients found."
                }
                onInputChange={(inputValue: string) =>
                  debouncedSetClientSearchTerm(inputValue)
                }
                onChange={onChange}
                required={true}
              />
            )}
          />
        </div>
      )}

      {/* Assignees filter */}
      {shouldShowFilter("assignees") && (
        <div className="filters-row">
          <Controller
            name="assignee_ids"
            control={control}
            render={({ field: { name, onChange, value } }) => (
              <MultiSelect
                name={name}
                label="Assignees"
                options={assigneeOptions}
                value={value}
                errors={errors}
                placeholder="Search for assignees..."
                noOptionsMessage={({ inputValue }) =>
                  !inputValue
                    ? "Begin typing to search for users..."
                    : isAssigneesFetching
                      ? "Searching..."
                      : "No users found."
                }
                onInputChange={(inputValue: string) =>
                  debouncedSetAssigneeSearchTerm(inputValue)
                }
                onChange={onChange}
                widthVariant="full"
                disableRemoveCondition={false}
              />
            )}
          />
        </div>
      )}

      {/* Status and Service Type row */}
      {(shouldShowFilter("status") || shouldShowFilter("service_type")) && (
        <div className="filters-row">
          {shouldShowFilter("status") && (
            <Controller
              name="status"
              control={control}
              render={({ field: { name, onChange, value } }) => (
                <Select
                  name={name}
                  label="Status"
                  options={statusOptions}
                  value={value}
                  errors={errors}
                  onChange={onChange}
                  placeholder="Select status..."
                />
              )}
            />
          )}
          {shouldShowFilter("service_type") && (
            <Controller
              name="service_type"
              control={control}
              render={({ field: { name, onChange, value } }) => (
                <Select
                  name={name}
                  label="Service Type"
                  options={serviceTypeOptions}
                  value={value}
                  errors={errors}
                  onChange={onChange}
                  placeholder="Select service type..."
                />
              )}
            />
          )}
        </div>
      )}

      {/* Created Date range */}
      {shouldShowFilter("created_dates") && (
        <div className="filters-row">
          <Controller
            name="created_after"
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <DatePicker
                name={name}
                initialDate={STRING_TO_DATE(value)}
                label="Created After"
                onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                errors={errors}
                allowFutureDates={true}
              />
            )}
          />
          <Controller
            name="created_before"
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <DatePicker
                name={name}
                label="Created Before"
                initialDate={STRING_TO_DATE(value)}
                onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                errors={errors}
                allowFutureDates={true}
              />
            )}
          />
        </div>
      )}

      {/* Started Date range */}
      {shouldShowFilter("started_dates") && (
        <div className="filters-row">
          <Controller
            name="started_after"
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <DatePicker
                name={name}
                initialDate={STRING_TO_DATE(value)}
                label="Started After"
                onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                errors={errors}
                allowFutureDates={true}
              />
            )}
          />
          <Controller
            name="started_before"
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <DatePicker
                name={name}
                label="Started Before"
                initialDate={STRING_TO_DATE(value)}
                onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                errors={errors}
                allowFutureDates={true}
              />
            )}
          />
        </div>
      )}

      {/* Completed Date range */}
      {shouldShowFilter("completed_dates") && (
        <div className="filters-row">
          <Controller
            name="completed_after"
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <DatePicker
                name={name}
                initialDate={STRING_TO_DATE(value)}
                label="Completed After"
                onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                errors={errors}
                allowFutureDates={true}
              />
            )}
          />
          <Controller
            name="completed_before"
            control={control}
            render={({ field: { value, name, onChange } }) => (
              <DatePicker
                name={name}
                label="Completed Before"
                initialDate={STRING_TO_DATE(value)}
                onDateChange={(date) => onChange(DATE_TO_STRING(date))}
                errors={errors}
                allowFutureDates={true}
              />
            )}
          />
        </div>
      )}
    </>
  );
};

export default TasksReportFilters;
